import { CardContainer, Container, Content, Heading, Line, Tab, TabContainer } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import WarningModal from "./warning-modal";
import GoogleCalendarModal from "./google-calendar-modal";
import { openGooglePopup } from "./google-oauth-popup";
import { useToast } from "hook/useToast";
import { openSlackPopup } from "./slack-oauth-popup";
import { connectSlackMessanger, disconnectSlackMessanger, getMessengers } from "apis/messangers";
import { ServiceIntegrationTab } from "uis/ServiceConnectManage";
import { disconnectPersonalCalendar, getPersonalCalendarConnection } from "apis/personal-calendar";
import { useRecoilValue } from "recoil";
import { member, workspace } from "recoil/recoilState";
import { LoadingModal } from "uis/modal";
import { getMemberAuthSyncSlack } from "apis/messenger-auto-sync";
import { superAdminRoles, UserRole } from "utils/role";
import { userHasAccess } from "pages/utils";
import { useLocation } from "react-router-dom";

type Messanger = "SLACK" | "GOOGLE";
const ServiceConnectManage = ({
  setIsLinkedSlack,
}: {
  setIsLinkedSlack?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const workspaceInfo = useRecoilValue(workspace);
  const memberInfo = useRecoilValue(member);
  const [openSlackUnlinkModal, setOpenSlackUnlinkModal] = useState<boolean>(false);
  const [openGoogleUnlinkModal, setOpenGoogleUnlinkModal] = useState<boolean>(false);
  const [openGoogleLinkModal, setOpenGoogleLinkModal] = useState<boolean>(false);
  const [googleToken, setGoogleToken] = useState<string>("");
  const [googleCalendarList, setGoogleCalendarList] = useState<{ id: string; name: string }[] | null>([]);
  const { showToast } = useToast();
  const [linkedMessengerInfo, setLinkedMessengerInfo] = useState<Messanger[]>();
  const [isLinkedGoogle, setIsLinkedGoogle] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSyncedBulkSlack, setIsSyncedBulkSlack] = useState<boolean>(false);
  const [tab, setTab] = useState<"member" | "admin">("member");
  const location = useLocation();

  useEffect(() => {
    //* navigator로 state를 전달받은 경우, 해당 탭으로 이동
    if (location.state?.tab === "admin") {
      setTab("admin");
    }
    if (location.search.includes("tab=admin")) {
      setTab("admin");
    }
    setInfo();
  }, []);

  const setInfo = async () => {
    getMessengers().then((res) => {
      if (res.success) {
        const messangers = res.data?.map((item) => item.messengerProvider);
        setLinkedMessengerInfo(messangers);
      }
    });
    getPersonalCalendarConnection().then((res) => {
      setIsLinkedGoogle(res.success);
    });

    getMemberAuthSyncSlack().then((res) => {
      if (res && res.slackWorkspaceId) {
        setIsSyncedBulkSlack(true);
      }
    });
  };

  const onDisconnectSlack = async () => {
    const res = await disconnectSlackMessanger();
    if (res.success) {
      showToast("슬랙 연동이 해제되었습니다.", "success");
      setInfo();
      setIsLinkedSlack?.(false);
    } else {
      showToast("슬랙 연동 해제에 실패했습니다. 다시 시도해주세요.", "error");
    }
    setOpenSlackUnlinkModal(false);
  };

  const onHandleSlackLink = () => {
    if (isSyncedBulkSlack) return;
    openSlackPopup(async (botToken, userId) => {
      const res = await connectSlackMessanger(botToken, userId);
      if (res.success) {
        showToast("슬랙이 연동되었습니다. Mile 서비스 알림을 슬랙으로 받아보세요!", "success");
        setInfo();
        setIsLinkedSlack?.(true);
      } else {
        showToast("슬랙 연동에 실패했습니다. 연동을 재시도해 주세요.", "error");
      }
    });
  };

  const onHandleSlackUnlink = () => {
    if (isSyncedBulkSlack) return;
    setOpenSlackUnlinkModal(true);
  };

  //INFO: 구글 캘린더 연동 해제 모달 띄우기
  const onHandleGoogleUnlink = () => {
    setOpenGoogleUnlinkModal(true);
  };

  //INFO: 구글 캘린더 연동
  const onHandleGoogleLink = () => {
    setLoading(true);
    openGooglePopup((success, data, refreshToken, message) => {
      if (success) {
        const filteredData = data.map((item: any) => {
          return {
            id: item.id,
            name: item.summary,
          };
        });
        setGoogleCalendarList(filteredData);
        setLoading(false);
      } else {
        setGoogleCalendarList(null);
        setLoading(false);
      }

      if (data !== "close") {
        setGoogleToken(refreshToken);
        setOpenGoogleLinkModal(true);
      }
    });
  };

  const onDisConnectGoogle = async () => {
    const res = await disconnectPersonalCalendar();
    if (res.success) {
      showToast("구글 캘린더 연동이 해제되었습니다.", "success");
      setInfo();
    } else {
      showToast("구글 캘린더 연동 해제에 실패했습니다. 다시 시도해주세요.", "error");
    }
    setOpenGoogleUnlinkModal(false);
  };

  return (
    <Container>
      <Heading className="heading">
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{
            padding: "24px 20px",
          }}
        >
          서비스 연동 관리
        </UI.Typography>
        <TabContainer className="tab-container">
          <Tab selected={tab === "member"} onClick={() => setTab("member")}>
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color={tab === "member" ? "var(--alias-color-primary-normal)" : "var(--alias-color-label-alternative)"}
            >
              구성원용
            </UI.Typography>
          </Tab>
          {userHasAccess(superAdminRoles, memberInfo?.role as UserRole) && (
            <Tab selected={tab === "admin"} onClick={() => setTab("admin")}>
              <UI.Typography
                type="body01 normal"
                fontStyle="bold"
                color={tab === "admin" ? "var(--alias-color-primary-normal)" : "var(--alias-color-label-alternative)"}
              >
                관리자용
              </UI.Typography>
            </Tab>
          )}
        </TabContainer>
      </Heading>
      {tab === "admin" && <ServiceIntegrationTab open={tab === "admin"} />}
      {tab === "member" && (
        <Content>
          <CardContainer className="slack">
            <div className="info">
              <div className="title">
                <UI.Icon name="slack" size={40} />
                <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-normal)">
                  Slack
                </UI.Typography>
              </div>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                슬랙 연동 시 Mile 서비스 알림을 슬랙으로 받아볼 수 있으며, 알림 기본 설정 값이 Slack으로 변경됩니다.
              </UI.Typography>
            </div>

            <Line className="line" />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                alignItems: "flex-start",
              }}
            >
              {isSyncedBulkSlack && (
                <UI.Typography type="label01" fontStyle="regular" color="var(--alias-color-label-alternative)">
                  ※ 관리자가 슬랙 일괄 연동 자동화를 활성화하여 개별 연동 및 해제가 불가합니다.
                </UI.Typography>
              )}
              {linkedMessengerInfo?.includes("SLACK") ? (
                <UI.Button
                  title="연동 해제하기"
                  type="outlined"
                  onClick={onHandleSlackUnlink}
                  primary={false}
                  disabled={isSyncedBulkSlack}
                />
              ) : (
                <UI.Button
                  title="연동하기"
                  size="medium"
                  type="solid"
                  primary={false}
                  onClick={onHandleSlackLink}
                  disabled={isSyncedBulkSlack}
                />
              )}
            </div>
          </CardContainer>
          {(workspaceInfo?.mileVisitorsUsage ?? false) && (
            <CardContainer className="google">
              <div className="info">
                <div className="title">
                  <UI.Icon name="google" size={40} />
                  <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-normal)">
                    Google Calendar
                  </UI.Typography>
                </div>
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  구글 개인 캘린더 연동 시 Mile Visitors에서 방문자를 등록하면 연동한 캘린더에 방문 일정이 바로
                  등록됩니다.
                </UI.Typography>
              </div>
              <Line className="line" />
              {isLinkedGoogle ? (
                <UI.Button
                  title="연동 해제하기"
                  type="outlined"
                  onClick={onHandleGoogleUnlink}
                  primary={false}
                  style={{
                    marginTop: "auto",
                  }}
                />
              ) : (
                <UI.Button
                  title="연동하기"
                  size="medium"
                  type="solid"
                  primary={false}
                  onClick={onHandleGoogleLink}
                  style={{
                    marginTop: "auto",
                  }}
                />
              )}
            </CardContainer>
          )}
        </Content>
      )}
      <WarningModal
        modalIsOpen={openSlackUnlinkModal}
        closeModal={() => setOpenSlackUnlinkModal(false)}
        onChange={onDisconnectSlack}
        title="슬랙 연동을 해제하시겠습니까?"
        btnTitle="해제하기"
        content={
          <p>
            [Mile Meeting]
            <br />
            슬랙으로 설정된 알림 채널이 e-mail로 일괄 변경됩니다.
            <br />
            [Mile Visitors]
            <br />
            서비스에서 발생하는 알림을 받아볼 수 없게 됩니다.
          </p>
        }
      />
      <WarningModal
        btnTitle="해제하기"
        modalIsOpen={openGoogleUnlinkModal}
        closeModal={() => setOpenGoogleUnlinkModal(false)}
        onChange={onDisConnectGoogle}
        title="구글 개인 캘린더 연동을 해제하시겠습니까?"
        content="개인 캘린더 일정 등록 기능, 외부 인원 일정 감지 기능을 사용할 수 없습니다."
      />
      <GoogleCalendarModal
        onUpdate={setInfo}
        googleToken={googleToken}
        modalIsOpen={openGoogleLinkModal}
        closeModal={() => setOpenGoogleLinkModal(false)}
        googleCalendarList={googleCalendarList}
      />
      <LoadingModal modalIsOpen={loading} />
    </Container>
  );
};

export default ServiceConnectManage;
